const MEDIA_KINDS = new Set(["image", "file", "audio", "video", "sticker"]);
export const EMPTY_MESSAGE_LABEL = "Message unavailable";
const STRUCTURED_KINDS = new Set([
  ...MEDIA_KINDS,
  "location",
  "contact",
  "template",
  "interactive",
]);

const REPLY_LABELS = {
  image: "Image",
  file: "File",
  audio: "Audio",
  video: "Video",
  sticker: "Sticker",
  location: "Location",
  contact: "Contact",
  template: "Template",
  interactive: "Interactive message",
};

export function parseWhatsAppMessageContent(value) {
  const raw = String(value ?? "").replace(/\r\n?/g, "\n").trim();
  if (!raw) {
    return {
      kind: "text",
      url: "",
      lines: [],
      filename: "",
      caption: "",
      raw: "",
    };
  }

  const [firstLine, ...remainingLines] = raw.split("\n");
  const marker = firstLine
    .trim()
    .match(/^\[(IMAGE|FILE|AUDIO|VIDEO|STICKER|LOCATION|CONTACT|TEMPLATE|INTERACTIVE)( PENDING)?\](?:\s+(.*))?$/i);

  if (!marker || marker[2]) {
    return {
      kind: "text",
      url: "",
      lines: [],
      filename: "",
      caption: "",
      raw,
    };
  }

  const kind = marker[1].toLowerCase();
  if (!STRUCTURED_KINDS.has(kind)) {
    return {
      kind: "text",
      url: "",
      lines: [],
      filename: "",
      caption: "",
      raw,
    };
  }

  const remainder = (marker[3] ?? "").trim();
  const following = remainingLines.map((line) => line.trim());

  if (MEDIA_KINDS.has(kind)) {
    return {
      kind,
      url: remainder,
      lines: following,
      filename: following[0] ?? "",
      caption: following.slice(1).join("\n").trim(),
      raw,
    };
  }

  return {
    kind,
    url: "",
    lines: remainder ? [remainder, ...following] : following,
    filename: "",
    caption: "",
    raw,
  };
}

export function safeHttpUrl(value) {
  const candidate = String(value ?? "").trim();
  if (!candidate) return null;

  try {
    const parsed = new URL(candidate);
    if (
      (parsed.protocol !== "http:" && parsed.protocol !== "https:") ||
      !parsed.hostname ||
      parsed.username ||
      parsed.password
    ) {
      return null;
    }
    return candidate;
  } catch {
    return null;
  }
}

function normalizeOrigin(value) {
  const url = safeHttpUrl(value);
  if (!url) return "";
  return new URL(url).origin;
}

function isPrivateOrLocalHostname(hostname) {
  const host = String(hostname ?? "")
    .toLowerCase()
    .replace(/^\[|\]$/g, "");
  if (
    !host ||
    host === "localhost" ||
    host.endsWith(".localhost") ||
    host.endsWith(".local") ||
    host.endsWith(".internal") ||
    host.endsWith(".lan") ||
    (!host.includes(".") && !host.includes(":"))
  ) {
    return true;
  }

  if (
    host === "::1" ||
    host.startsWith("::ffff:") ||
    host.startsWith("fc") ||
    host.startsWith("fd") ||
    /^fe[89ab]/.test(host)
  ) {
    return true;
  }

  const ipv4 = host.split(".").map(Number);
  if (ipv4.length !== 4 || ipv4.some((part) => !Number.isInteger(part))) {
    return false;
  }
  const [first, second] = ipv4;
  return (
    first === 0 ||
    first === 10 ||
    first === 127 ||
    (first === 100 && second >= 64 && second <= 127) ||
    (first === 169 && second === 254) ||
    (first === 172 && second >= 16 && second <= 31) ||
    (first === 192 && second === 168) ||
    (first === 198 && (second === 18 || second === 19))
  );
}

export function createTrustedMediaOrigins(currentOrigin, ...configuredUrls) {
  const sameOrigin = normalizeOrigin(currentOrigin);
  const configuredOrigins = [
    ...new Set(
      configuredUrls
        .flat()
        .map(normalizeOrigin)
        .filter((origin) => origin && origin !== sameOrigin)
    ),
  ];
  return { sameOrigin, configuredOrigins };
}

export function isTrustedMediaUrl(value, allowedOrigins) {
  const safeUrl = safeHttpUrl(value);
  if (!safeUrl) return false;

  const parsed = new URL(safeUrl);
  if (parsed.origin === allowedOrigins?.sameOrigin) return true;
  if (isPrivateOrLocalHostname(parsed.hostname)) return false;
  return Boolean(allowedOrigins?.configuredOrigins?.includes(parsed.origin));
}

function basenameFromUrl(value) {
  const safeUrl = safeHttpUrl(value);
  if (!safeUrl) return "";
  try {
    return decodeURIComponent(new URL(safeUrl).pathname.split("/").pop() || "");
  } catch {
    return "";
  }
}

function isPlainFilename(value) {
  const candidate = String(value ?? "").trim();
  return (
    Boolean(candidate) &&
    !/^[a-z][a-z0-9+.-]*:/i.test(candidate) &&
    !/[\\/]/.test(candidate)
  );
}

export function buildWhatsAppMediaPresentation(content, allowedOrigins) {
  const kind = MEDIA_KINDS.has(content?.kind) ? content.kind : "file";
  const safeUrl = safeHttpUrl(content?.url);
  const explicitFilename = String(content?.filename ?? "").trim();
  const caption = String(content?.caption ?? "").trim();
  const fallbackFilename = safeUrl
    ? basenameFromUrl(safeUrl)
    : kind === "file" && isPlainFilename(content?.url)
      ? String(content.url).trim()
      : "";
  const filename = explicitFilename || fallbackFilename;
  const typeLabel = `${kind.charAt(0).toUpperCase()}${kind.slice(1)}`;
  const ariaLabel = explicitFilename
    ? `${typeLabel}: ${explicitFilename}`
    : caption
      ? `${typeLabel}: ${caption.split("\n")[0]}`
      : fallbackFilename
        ? `${typeLabel}: ${fallbackFilename}`
        : `${typeLabel} message`;

  let mode = "unavailable";
  if (safeUrl) {
    if (kind === "image") {
      mode = "embedded";
    } else if (kind === "file") {
      mode = "link";
    } else {
      mode = isTrustedMediaUrl(safeUrl, allowedOrigins) ? "embedded" : "link";
    }
  } else if (kind === "file" && fallbackFilename) {
    mode = "file-label";
  }

  return {
    kind,
    mode,
    embeddedUrl: mode === "embedded" ? safeUrl : "",
    linkUrl: mode === "link" ? safeUrl : "",
    filename,
    caption,
    ariaLabel,
    linkLabel: `Open ${kind}`,
  };
}

export function parseLocationCoordinates(lines) {
  for (const line of Array.isArray(lines) ? lines : []) {
    const match = String(line)
      .trim()
      .match(/^([+-]?(?:\d+(?:\.\d*)?|\.\d+))\s*,\s*([+-]?(?:\d+(?:\.\d*)?|\.\d+))$/);
    if (!match) continue;

    const latitude = Number(match[1]);
    const longitude = Number(match[2]);
    if (
      !Number.isFinite(latitude) ||
      !Number.isFinite(longitude) ||
      latitude < -90 ||
      latitude > 90 ||
      longitude < -180 ||
      longitude > 180
    ) {
      continue;
    }

    return {
      latitude,
      longitude,
      mapUrl: `https://www.google.com/maps?q=${encodeURIComponent(
        `${latitude},${longitude}`
      )}`,
    };
  }

  return null;
}

export function replyPreviewLabel(value) {
  const parsed = parseWhatsAppMessageContent(value);
  if (parsed.kind === "file" && parsed.filename) {
    return `File: ${parsed.filename}`;
  }
  return REPLY_LABELS[parsed.kind] ?? "";
}
